/**
 * Flows to account for:
 * - User is on a chat page, chat gets loaded in SSR, needs to populate the store on client
 * - User submits prompt on homepage, needs to navigate to chat page once there's a chatId while keeping the streaming alive
 * - User navigates to a chat page, needs to fetch the chat from the server and populate the store on the client
 * - User navigates away from a chat page, needs to stop the streaming and clear the store
 * - User scrolls up in a chat, there's more messages to load, needs to fetch them and add to the store. UI needs to load them seamlessly while preserving the scroll position.
 */

import { type Params, useNavigate } from "@solidjs/router";
import { stAnalytics } from "@storytell/analytics";
import {
  type ChatPrompt,
  type Features,
  type LanguageRegions,
  type RichText,
  type Scope,
  newStreamRequest,
} from "@storytell/cp-client";
import {
  createConversationRequest,
  createInterruptChatRequest,
  useCallOptions,
  useChatClient,
  withBearerToken,
} from "@storytell/cp-client";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import {
  type StreamingBlock,
  type StreamingBlockId,
  type StreamingChatId,
  StreamingChatStatus,
  type StreamingMessage,
  type StreamingMessageId,
  type StreamingOperation,
  type StreamingOperationId,
  type StreamingService,
} from "@storytell/streaming";
import { createMemo, createRenderEffect, onCleanup, onMount, untrack } from "solid-js";
import { type SetStoreFunction, createStore } from "solid-js/store";
import { urls } from "~/lib/urls";
import type { AssetsService } from "~/stores/assets";
import { createChatServiceResource } from "./chatServiceResource";
import { createChatServiceScope } from "./chatServiceScope";

export type ChatService = ReturnType<typeof createChatService>;

export type ChatServiceState = {
  chatId: StreamingChatId | undefined;
  error: Error | undefined;
  isStreaming: boolean;
  currentStreamingMessageId: StreamingMessageId | undefined;
  isInterrupting: boolean;
};
export type SetChatServiceState = SetStoreFunction<ChatServiceState>;

/**
 * High-level manager for handling chat functionality.
 * Resposible for:
 * - Navigation between chats
 * - Chat creation
 * - Chat playback
 * - UI state management
 *
 * The logic for storing and streaming chats is handled by the streaming package.
 * This service wraps extra logic around it.
 */
export const createChatService = (deps: {
  urlParams: () => Params;
  authReady: () => boolean;
  authToken: () => string | undefined;
  projectId: () => string | undefined;
  userId: () => string | undefined;
  stream: StreamingService;
  reloadBillingInfo: () => void;
  assets: AssetsService;
  generateChatTitle?: (chatId: StreamingChatId, message: StreamingMessage, model?: Features.Feature) => Promise<void>;
  setChatTitle?: (chatId: StreamingChatId, title: string) => void;
}) => {
  const navigate = useNavigate();

  const [state, setState] = createStore<ChatServiceState>({
    chatId: deps.urlParams().chatId as StreamingChatId | undefined,
    error: undefined,
    isStreaming: false,
    currentStreamingMessageId: undefined,
    isInterrupting: false,
  });

  const resource = createChatServiceResource({
    authReady: deps.authReady,
    authToken: deps.authToken,
    urlParams: deps.urlParams,
    setChatTitle: deps.setChatTitle,
    state,
    setState,
    stream: deps.stream,
    userId: deps.userId,
  });

  const scope = createChatServiceScope({
    projectId: () => deps.projectId(),
    assets: deps.assets,
  });

  /**
   * Poll for chat title after chat creation
   * Polls the conversation API every 2 seconds to check if the backend has generated a title
   * Continues polling while message is streaming, then gives a grace period after completion
   */
  const pollForChatTitle = (chatId: StreamingChatId, isStreamingFn: () => boolean) => {
    const MAX_POST_STREAMING_ATTEMPTS = 10; // After streaming stops, poll for 20 more seconds
    const POLL_INTERVAL = 2000; // Poll every 2 seconds
    let totalAttempts = 0;
    let postStreamingAttempts = 0;
    let pollInterval: ReturnType<typeof setInterval> | undefined;

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = undefined;
      }
    };

    const checkTitle = async () => {
      totalAttempts++;
      const isStreaming = isStreamingFn();

      if (isStreaming) {
        // Reset post-streaming counter while still streaming
        postStreamingAttempts = 0;
      } else {
        // Start counting post-streaming attempts
        postStreamingAttempts++;
      }

      try {
        const chatClient = useChatClient();
        const token = deps.authToken();

        if (!token) {
          stopPolling();
          return;
        }

        const conversation = await chatClient.conversation(
          createConversationRequest({
            chatId: chatId,
            recentMessagesCount: BigInt(1),
          }),
          useCallOptions(withBearerToken(token)),
        );

        if (conversation.isOk()) {
          const title = conversation.value.title?.trim() || "";
          const isPlaceholder = !title || title === "New" || title === "New Chat";

          if (!isPlaceholder) {
            // Update ChatTitleService
            if (deps.setChatTitle) {
              deps.setChatTitle(chatId, title);
            }

            // Dispatch event for sidebar
            dispatchGlobalEvent("chat-title-generated", {
              chatId,
              title,
            });

            stopPolling();
          } else {
            // Only enforce timeout after streaming has stopped
            if (!isStreaming && postStreamingAttempts >= MAX_POST_STREAMING_ATTEMPTS) {
              stopPolling();
            }
          }
        } else {
          // Only enforce timeout after streaming has stopped
          if (!isStreaming && postStreamingAttempts >= MAX_POST_STREAMING_ATTEMPTS) {
            stopPolling();
          }
        }
      } catch (error) {
        // Only enforce timeout after streaming has stopped
        if (!isStreaming && postStreamingAttempts >= MAX_POST_STREAMING_ATTEMPTS) {
          stopPolling();
        }
      }
    };

    // Start polling
    checkTitle();
    pollInterval = setInterval(checkTitle, POLL_INTERVAL);
  };

  /**
   * Main handler for submitting a prompt
   */
  const startOrContinueChat = (opts: {
    submission: RichText.RichText;
    locale: LanguageRegions.LanguageRegion;
    projectId: string;
    workspaceId: string;
    scope: Scope.Scope;
    model?: Features.Feature;
  }) => {
    const chat = getChat();
    const chatId = chat?.id;

    const parentMessageId = chat?.messages[0];

    setState("isStreaming", true);

    const request = newStreamRequest(
      chatId || "",
      opts.submission,
      opts.locale,
      false,
      opts.projectId,
      opts.workspaceId,
      opts.scope,
      parentMessageId,
      opts.model,
    );
    const token = deps.authToken();

    let capturedOnce = false;
    const onChatIdCaptured = (chatId: StreamingChatId) => {
      if (capturedOnce) return;
      dispatchGlobalEvent("new-chat-created", {
        id: chatId,
        title: "New Chat",
      });
      dispatchGlobalEvent("prompt-submitted-successfuly", undefined);
      capturedOnce = true;
      setState({
        chatId,
        error: undefined,
      });
      navigate(urls.chatPage(chatId));

      // Start polling for backend-generated title
      // Pass a function that checks if this specific chat is still streaming
      pollForChatTitle(chatId, () => {
        return state.isStreaming || deps.stream.chat.getStatus(chatId) === StreamingChatStatus.GENERATING;
      });
    };

    const onMessageStarted = (messageId: StreamingMessageId) => {
      setState("currentStreamingMessageId", messageId);
    };

    deps.stream.chat.startStreamingChat({
      request: request,
      options: {
        bearerToken: token,
      },
      onChatIdCaptured,
      onMessageStarted,
      onMessageFinished: async (messageId) => {
        setState("isStreaming", false);
        setState("currentStreamingMessageId", undefined);
        deps.reloadBillingInfo();

        try {
          const message = deps.stream.message.get(messageId);
          if (!message) return;
          const cost = message.aggregateMetrics?.totalCredits ?? 0;
          stAnalytics.track("credits_consumed", {
            reason: "stream_response",
            amount: cost,
          });

          // Title polling is now handled in onChatIdCaptured
        } catch (err) {}
      },
    });
  };

  const getChatId = () => state.chatId;
  const getChat = createMemo(() => (state.chatId ? deps.stream.state.chats[state.chatId] : undefined));
  const getChatError = () => state.error;
  const isChatStreaming = () =>
    state.isStreaming || (state.chatId && deps.stream.chat.getStatus(state.chatId) === StreamingChatStatus.GENERATING);
  const isChatIdle = () => !isChatStreaming();
  const isChatLoading = () => resource.loading;
  const isInterrupting = () => state.isInterrupting;

  /**
   * Set the scope from the last message when loading a chat
   * This runs after the chat messages have been processed and populated
   */
  createRenderEffect(() => {
    const chat = getChat();
    if (chat && chat.messages.length > 0) {
      const lastMessageId = chat.messages[chat.messages.length - 1];
      const lastMessage = deps.stream.state.messages[lastMessageId];
      const messageScope = lastMessage?.prompt.scope;
      if (messageScope) {
        untrack(() => {
          scope.loadScopeFromMessage(messageScope);
        });
        return;
      }

      scope.clearAll();
    }
  });

  const shouldShowContent = () => {
    if (getChatId()) return true;
    if (isChatStreaming()) return true;
    if (isChatLoading()) return true;

    return false;
  };

  const getOperation = (operationId: StreamingOperationId) => deps.stream.operation.get(operationId);
  const getMessageOperations = (messageId: StreamingMessageId) =>
    deps.stream.message
      .get(messageId)
      ?.operations.map((op) => deps.stream.operation.get(op))
      .filter((op): op is StreamingOperation => op !== undefined);
  const getMessage = (messageId: StreamingMessageId) => deps.stream.message.get(messageId);
  const getChatMessages = () =>
    getChatId()
      ? deps.stream.chat
          .get(getChatId()!)
          ?.messages.map((m) => deps.stream.message.get(m))
          .filter((m): m is StreamingMessage => m !== undefined)
      : undefined;
  const getBlock = (blockId: StreamingBlockId) => deps.stream.block.get(blockId);
  const getOperationBlocks = (operationId: StreamingOperationId) =>
    deps.stream.operation
      .get(operationId)
      ?.blocks.map((b) => deps.stream.block.get(b))
      .filter((b): b is StreamingBlock => b !== undefined);
  const getOperationTask = (operationId: StreamingOperationId) => deps.stream.operation.get(operationId)?.task;

  const interruptStream = async (userInput = "") => {
    const messageId = state.currentStreamingMessageId;
    if (!messageId) {
      console.warn("No active streaming message to interrupt");
      return;
    }

    if (state.isInterrupting) {
      console.warn("Interrupt already in progress");
      return;
    }

    const token = deps.authToken();
    if (!token) {
      console.error("No auth token available for interrupt request");
      return;
    }

    try {
      setState("isInterrupting", true);

      const chatClient = useChatClient();
      const request = createInterruptChatRequest({
        messageId: messageId,
      });

      const result = await chatClient.interruptChat(request, useCallOptions(withBearerToken(token)));

      if (!result.isOk()) {
        console.error("Failed to interrupt chat:", result.error);
        throw new Error(`Failed to interrupt chat: ${result.error.message}`);
      }

      if (result.value.success) {
        console.log("Successfully sent interrupt signal to backend");

        // Get the message ID before clearing it
        const interruptedMessageId = state.currentStreamingMessageId;

        // Clean up streaming state after successful interruption
        setState("isStreaming", false);
        setState("currentStreamingMessageId", undefined);

        // Since the backend doesn't send onMessageFinished when interrupted,
        // we need to manually clean up the UI state
        if (interruptedMessageId && state.chatId) {
          // Force the streaming store to update by dispatching a custom event
          // This ensures the UI reflects that streaming has stopped
          deps.reloadBillingInfo();

          // Log metrics for the interrupted message if available
          try {
            const message = deps.stream.message.get(interruptedMessageId);
            if (message) {
              const cost = message.aggregateMetrics?.totalCredits ?? 0;
              stAnalytics.track("credits_consumed", {
                reason: "stream_response",
                amount: cost,
              });
            }
          } catch (err) {
            console.error("Failed to track interrupted message metrics:", err);
          }
        }
      } else {
        console.warn("Interrupt request was not successful");
        throw new Error("Failed to interrupt chat: Request was not successful");
      }
    } catch (error) {
      console.error("Failed to interrupt stream:", error);
      throw error;
    } finally {
      setState("isInterrupting", false);
    }
  };

  const resetChat = () => {
    scope.clearAll();
    setState({
      chatId: undefined,
      error: undefined,
      isStreaming: false,
      currentStreamingMessageId: undefined,
      isInterrupting: false,
    });
  };

  onMount(() => {
    const unsubscribeUserContextChanged = subscribeToGlobalEvent("user-context-changed", () => {
      resetChat();
    });
    onCleanup(() => {
      unsubscribeUserContextChanged();
    });
  });

  return {
    suspenseTrigger: () => (resource() ? undefined : undefined),
    scope,
    getChatId,
    shouldShowContent,
    getChat,
    getChatError,
    isChatStreaming,
    isChatIdle,
    isChatLoading,
    isInterrupting,
    startOrContinueChat,
    interruptStream,
    resetChat,
    getOperation,
    getMessageOperations,
    getMessage,
    getChatMessages,
    getBlock,
    getOperationBlocks,
    getOperationTask,
  };
};
